import type { ApiResponse } from '../types';
import { resolveApiUrl } from './api-url';

const API_URL = resolveApiUrl();

/**
 * API клиент для прямых запросов к backend
 */
export const apiClient = {
  /**
   * GET запрос
   */
  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка запроса');
    }

    const json = await response.json() as ApiResponse<T>;

    if (!json.success || json.data === null) {
      throw new Error(json.errors?.[0] ?? 'Ошибка получения данных');
    }

    return json.data;
  },

  /**
   * GET запрос с сохранением meta (пагинация: total/page/pageSize)
   */
  async getWithMeta<T>(endpoint: string): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка запроса');
    }

    const json = await response.json() as ApiResponse<T>;

    if (!json.success || json.data === null) {
      throw new Error(json.errors?.[0] ?? 'Ошибка получения данных');
    }

    return { data: json.data, meta: json.meta };
  },

  /**
   * POST запрос
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка запроса');
    }

    const json = await response.json() as ApiResponse<T>;

    if (!json.success || json.data === null) {
      throw new Error(json.errors?.[0] ?? 'Ошибка сохранения данных');
    }

    return json.data;
  },

  /**
   * PUT запрос
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка запроса');
    }

    const json = await response.json() as ApiResponse<T>;

    if (!json.success || json.data === null) {
      throw new Error(json.errors?.[0] ?? 'Ошибка обновления данных');
    }

    return json.data;
  },

  /**
   * DELETE запрос
   */
  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка запроса');
    }

    const json = await response.json() as ApiResponse<T>;

    if (!json.success || json.data === null) {
      throw new Error(json.errors?.[0] ?? 'Ошибка удаления данных');
    }

    return json.data;
  },

  /**
   * GET запрос для скачивания файла
   */
  async download(endpoint: string, filename?: string): Promise<void> {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Ошибка сервера' }));
      throw new Error(error.errors?.[0] ?? error.message ?? 'Ошибка скачивания');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? 'download';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
